import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMe } from '../features/authSlice';
import { useStateContext } from "../contexts/ContextProvider";

import {
  FaEdit,
  FaTrash,
  FaUsers,
  FaPlus,
  FaSearch,
  FaTimesCircle,
  FaLeaf,
} from "react-icons/fa";

const ListProgram = () => {
  const [program, setProgram] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { currentColor, currentMode } = useStateContext();

  // Colors
  const isDark = currentMode === "Dark";
  const accentColor = currentColor || "#c9862a";
  const bgColor = isDark ? "#1a1a18" : "#f7f3ec";
  const cardBg = isDark ? "#242420" : "#ffffff";
  const borderColor = isDark ? "#2e2e28" : "#e8e0d0";
  const textPrimary = isDark ? "#f0ece3" : "#1a1a18";
  const textSecondary = isDark ? "#a09880" : "#6b6456";
  const inputBg = isDark ? "#1a1a18" : "#ffffff";
  const inputBorder = isDark ? "#3a3a34" : "#e8e0d0";

  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      getProgram();
    } else {
      navigate('/');
    }
  }, [navigate, user]);

  const getProgram = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_URL_API;
      const response = await axios.get(`${apiUrl}/program`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgram(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Filter PROGRAM based on search
  const filteredPrograms = program.filter((program) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      program.judul.toLowerCase().includes(searchLower)
    );
  });

  // Truncate description
  const truncateText = (text, maxLength = 80) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Handle delete click
  const handleDeleteClick = (program) => {
    setSelectedProgram(program);
    setShowDeleteModal(true);
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedProgram(null);
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_URL_API;
      await axios.delete(`${apiUrl}/program/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteModal(false);
      setSelectedProgram(null);
      getProgram();
    } catch (error) {
      console.error('Error deleting program:', error);
    }
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
      {/* Background Pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-5">
          <FaUsers
            className="absolute bottom-40 left-20 text-7xl"
            style={{ color: accentColor }}
          />
          <FaLeaf
            className="absolute top-60 right-1/4 text-5xl"
            style={{ color: accentColor }}
          />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div
            className="p-6 rounded-xl border"
            style={{
              backgroundColor: cardBg,
              borderColor: borderColor,
            }}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div
                  className="p-3 rounded-xl"
                  style={{ backgroundColor: accentColor }}
                >
                  <FaUsers className="text-white text-2xl" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-serif text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
                      Program Management
                    </span>
                    <span className="text-xs" style={{ color: textSecondary }}>—</span>
                    <span className="font-serif text-xs font-bold" style={{ color: accentColor }}>
                      Rumoh Pangan Aceh
                    </span>
                  </div>
                  <h1 className="text-2xl font-serif font-bold" style={{ color: textPrimary }}>
                    Data Program
                  </h1>
                  <p className="text-sm mt-1" style={{ color: textSecondary }}>
                    Manajemen program Rumoh Pangan Aceh
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate("/add-program")}
                className="flex items-center justify-center gap-2 px-6 py-3 text-white rounded-xl transition-all duration-300 hover:shadow-lg font-medium"
                style={{ backgroundColor: accentColor }}
              >
                <FaPlus className="text-sm" />
                Tambah Program
              </button>
            </div>
          </div>
        </motion.div>

        {/* Search Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6"
        >
          <div
            className="p-4 rounded-xl border"
            style={{
              backgroundColor: cardBg,
              borderColor: borderColor,
            }}
          >
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="relative flex-1 w-full">
                <FaSearch
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-sm"
                  style={{ color: textSecondary }}
                />
                <input
                  type="text"
                  placeholder="Cari nama atau role..."
                  className="w-full pl-11 pr-4 py-3 rounded-lg border transition-all duration-300 outline-none"
                  style={{
                    backgroundColor: inputBg,
                    borderColor: inputBorder,
                    color: textPrimary,
                  }}
                  onFocus={(e) => e.target.style.borderColor = accentColor}
                  onBlur={(e) => e.target.style.borderColor = inputBorder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Data Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="rounded-xl border overflow-hidden"
          style={{
            backgroundColor: cardBg,
            borderColor: borderColor,
          }}
        >
          {/* Table Header */}
          <div
            className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b"
            style={{
              backgroundColor: isDark ? "#1a1a18" : `${accentColor}08`,
              borderColor: borderColor,
            }}
          >
            <div className="col-span-1 text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
              No
            </div>
            <div className="col-span-3 text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
              Judul
            </div>
            <div className="col-span-6 text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
              Deskripsi
            </div>
            <div className="col-span-1 text-center text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
              Actions
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y" style={{ borderColor: borderColor }}>
            {filteredPrograms.length > 0 ? (
              filteredPrograms.map((program, index) => (
                <motion.div
                  key={program.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 items-center hover:shadow-md transition-all duration-300"
                >
                  {/* No */}
                  <div className="col-span-1 flex md:block items-center">
                    <span
                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold dark:text-white"
                    >
                      {index + 1}
                    </span>
                  </div>

                  {/* Nama */}
                  <div className="col-span-3">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-sm" style={{ color: textPrimary }}>
                        {program.judul}
                      </span>
                    </div>
                  </div>

                  {/* Deskripsi */}
                  <div className="col-span-6">
                    <p className="text-sm" style={{ color: textSecondary }}>
                      {truncateText(program.deskripsi)}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex justify-center">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/program/edit/${program.id}`)}
                        className="p-2 rounded-lg transition-all duration-300 hover:scale-110"
                        style={{
                          backgroundColor: `${accentColor}15`,
                          color: accentColor,
                        }}
                      >
                        <FaEdit className="text-sm" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(program)}
                        className="p-2 rounded-lg transition-all duration-300 hover:scale-110"
                        style={{
                          backgroundColor: "#fef2f2",
                          color: "#dc2626",
                        }}
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="py-16 text-center">
                <FaUsers
                  className="text-6xl mb-4 mx-auto opacity-30"
                  style={{ color: textSecondary }}
                />
                <h3 className="text-lg font-semibold mb-2" style={{ color: textPrimary }}>
                  Tidak ada program ditemukan
                </h3>
                <p className="text-sm mb-6" style={{ color: textSecondary }}>
                  {searchTerm
                    ? "Coba ubah kata kunci pencarian"
                    : "Tambahkan program baru untuk memulai"}
                </p>
                {!searchTerm && (
                  <button
                    onClick={() => navigate("/add-program")}
                    className="inline-flex items-center gap-2 px-6 py-3 text-white rounded-xl transition-all duration-300 font-medium"
                    style={{ backgroundColor: accentColor }}
                  >
                    <FaPlus className="text-sm" />
                    Tambah Program
                  </button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedProgram && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleDeleteCancel}
          ></div>

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl overflow-hidden z-10"
            style={{
              backgroundColor: cardBg,
              border: `1px solid ${borderColor}`,
            }}
          >
            {/* Modal Header */}
            <div
              className="px-6 py-4"
              style={{
                background: `linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)`,
              }}
            >
              <div className="flex items-center gap-3">
                <FaTimesCircle className="h-6 w-6 text-white" />
                <h3 className="text-lg font-bold font-serif text-white">
                  Hapus Program
                </h3>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6">
              <div className="flex items-start gap-4">
                <div
                  className="p-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: "#fef2f2" }}
                >
                  <FaTrash className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm leading-relaxed mb-3" style={{ color: textPrimary }}>
                    Apakah Anda yakin ingin menghapus program <strong>{selectedProgram.judul}</strong> dari Rumoh Pangan Aceh?
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="px-6 py-4 border-t flex gap-3 justify-end"
              style={{
                backgroundColor: isDark ? "#1a1a18" : "#f7f3ec",
                borderColor: borderColor,
              }}
            >
              <button
                type="button"
                onClick={handleDeleteCancel}
                className="px-5 py-2.5 border rounded-xl text-sm font-medium transition-all duration-300"
                style={{
                  borderColor: borderColor,
                  color: textPrimary,
                  backgroundColor: cardBg,
                }}
              >
                Batal
              </button>
              <button
                type="button"
                onClick={() => handleDelete(selectedProgram.id)}
                className="px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all duration-300"
                style={{ backgroundColor: "#dc2626" }}
              >
                Hapus Program
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default ListProgram
